"use client";
import type { AnalyticsEvent } from "@/lib/analytics/events";

declare global {
  interface Window {
    // dataLayer holds both event objects (from pushEvent) and arguments arrays
    // (from the gtag shim used for Consent Mode v2 — that's how Google's
    // official snippet posts gtag('consent', …) into the same queue).
    dataLayer?: unknown[];
  }
}

export function pushEvent(event: AnalyticsEvent, payload: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({ event, ...payload });
}

// sessionStorage-backed dedupe for once-per-page-load events (e.g. view_sticker_pack)
// so a remount or a soft nav back doesn't double-count.
export function pushEventOnce(
  event: AnalyticsEvent,
  dedupeKey: string,
  payload: Record<string, unknown> = {},
) {
  if (typeof window === "undefined") return;
  const storageKey = `sticko_evt_once:${event}:${dedupeKey}`;
  try {
    if (window.sessionStorage.getItem(storageKey)) return;
    window.sessionStorage.setItem(storageKey, "1");
  } catch {
    // sessionStorage blocked (private mode, quota) — fall through and fire anyway.
  }
  pushEvent(event, payload);
}

// gtag shim that routes to the same dataLayer GTM reads from. Used for
// Consent Mode v2 updates from React (`gtag('consent','update', …)`).
export function gtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push(args);
}
